import {
  ATTACH_MAX_BYTES,
  fmtSize,
  MAX_PLAN_BYTES,
  MAX_PLANS,
  PLAN_ACCEPT,
  planExt,
  plansMissed,
  roDateTime,
  type Rfq,
  t,
} from '@procumi/shared';
import { useState } from 'react';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { ask } from '../lib/confirm';
import { autoSendAllowed } from '../lib/me';
import { isBuild } from '../lib/terms';

/**
 * Planurile și documentele cererii (arhitectură, cofraj, liste de cantități):
 * pleacă la toți furnizorii. `PlanPicker` le ține la crearea cererii, până se
 * salvează; `PlansSection` le arată pe cerere, unde se pun și se șterg oricând.
 */

const MB = 1024 * 1024;

/** De ce nu se poate pune fișierul; null = se poate. Serverul verifică la fel. */
export function planProblem(file: File): string | null {
  if (!planExt(file.name)) {
    return t(
      '„{0}” nu e un tip acceptat. Merg PDF, ZIP, RAR, 7Z, DWG, DXF, imagini, Excel și Word.',
      file.name,
    );
  }
  if (file.size > MAX_PLAN_BYTES) {
    return t(
      '„{0}” are {1}. Maximul e {2} MB pe fișier.',
      file.name,
      fmtSize(file.size),
      MAX_PLAN_BYTES / MB,
    );
  }
  return null;
}

/** Cum ajung planurile la furnizor, pe scurt, după pachet și mărime. */
function howTheyTravel(total: number): string {
  if (!autoSendAllowed()) {
    return t('Atașează-le în mailul pe care îl trimiți furnizorilor; le descarci de aici.');
  }
  return total <= ATTACH_MAX_BYTES
    ? t('Pleacă atașate la mailul din Procumi, la toți furnizorii.')
    : t(
        'Au împreună peste {0} MB, deci pleacă în mail ca linkuri de descărcare, nu atașate: așa trec de limitele serverelor de mail.',
        ATTACH_MAX_BYTES / MB,
      );
}

function Drop({ busy, onFiles }: { busy: boolean; onFiles: (files: File[]) => void }) {
  const [over, setOver] = useState(false);
  return (
    <div
      className={`drop${over ? ' over' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        onFiles([...e.dataTransfer.files]);
      }}
    >
      <span>
        {isBuild()
          ? t('Trage aici planurile: PDF, ZIP, DWG, imagini, Excel')
          : t('Trage aici documentele: PDF, ZIP, imagini, Excel')}
      </span>
      <label className="btn small">
        {busy ? t('Se urcă…') : t('Alege fișiere')}
        <input
          type="file"
          accept={PLAN_ACCEPT}
          multiple
          hidden
          disabled={busy}
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            e.target.value = '';
            onFiles(files);
          }}
        />
      </label>
    </div>
  );
}

/** La crearea cererii: fișierele așteaptă în browser până se salvează cererea. */
export function PlanPicker({
  files,
  onChange,
  toast,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  toast: Toast;
}) {
  const add = (incoming: File[]) => {
    const ok: File[] = [];
    for (const f of incoming) {
      const problem = planProblem(f);
      if (problem) toast(problem);
      else ok.push(f);
    }
    const next = [...files, ...ok];
    if (next.length > MAX_PLANS) toast(t('O cerere poate avea cel mult {0} planuri.', MAX_PLANS));
    onChange(next.slice(0, MAX_PLANS));
  };
  const total = files.reduce((n, f) => n + f.size, 0);
  return (
    <div>
      {files.length ? (
        <div className="docs">
          {files.map((f, i) => (
            <div className="doc" key={`${f.name}-${i}`}>
              <div className="nm">
                {f.name}
                <span>{fmtSize(f.size)}</span>
              </div>
              <button className="btn small" onClick={() => onChange(files.filter((_, k) => k !== i))}>
                {t('Scoate')}
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <Drop busy={false} onFiles={add} />
      <p className="hint">
        {files.length ? howTheyTravel(total) : t('Opțional. Furnizorii le primesc odată cu cererea.')}
      </p>
    </div>
  );
}

/** Pune fișierele unul după altul; întoarce cererea de după ultimul pus. */
export async function uploadPlans(
  rfqId: string,
  files: File[],
): Promise<{ rfq: Rfq | null; failed: string[] }> {
  let rfq: Rfq | null = null;
  const failed: string[] = [];
  for (const f of files) {
    try {
      rfq = await api.uploadRfqFile(rfqId, f);
    } catch (e) {
      failed.push(`${f.name} (${errMsg(e)})`);
    }
  }
  return { rfq, failed };
}

/** Pe pagina cererii: planurile puse, cine nu le-a primit, și locul unde se pun altele. */
export function PlansSection({
  rfq,
  onUpdated,
  toast,
}: {
  rfq: Rfq;
  onUpdated: (rfq: Rfq) => void;
  toast: Toast;
}) {
  const [busy, setBusy] = useState(false);
  const files = rfq.files ?? [];
  const total = files.reduce((n, f) => n + f.size, 0);

  const add = async (incoming: File[]) => {
    const ok = incoming.filter((f) => {
      const problem = planProblem(f);
      if (problem) toast(problem);
      return !problem;
    });
    if (!ok.length) return;
    setBusy(true);
    const r = await uploadPlans(rfq.id, ok);
    setBusy(false);
    if (r.rfq) onUpdated(r.rfq);
    toast(
      r.failed.length
        ? t('Nu s-au pus: {0}', r.failed.join(', '))
        : ok.length === 1
          ? t('Planul a fost pus la cerere')
          : t('Au fost puse {0} planuri', ok.length),
    );
  };

  const remove = async (id: string, name: string) => {
    const yes = await ask({
      title: t('Ștergi planul?'),
      message: t(
        '„{0}” dispare de pe cerere și de pe pagina furnizorilor. Cei care l-au primit deja în mail îl păstrează.',
        name,
      ),
      confirm: t('Șterge planul'),
      danger: true,
    });
    if (!yes) return;
    try {
      onUpdated(await api.deleteRfqFile(rfq.id, id));
      toast(t('Planul a fost șters'));
    } catch (e) {
      toast(errMsg(e));
    }
  };

  return (
    <section className="sheet">
      <div className="pad">
        <div className="section-title">
          <h2>{t('Planuri și documente')}</h2>
          {files.length ? (
            <span className="muted" style={{ fontSize: '.9rem' }}>
              {files.length} · {fmtSize(total)}
            </span>
          ) : null}
        </div>
        {files.length ? (
          <div className="docs">
            {files.map((f) => {
              const missed = rfq.sends.filter((s) => plansMissed([f], s.sentAt).length && !s.offer);
              return (
                <div className="doc" key={f.id}>
                  <div className="nm">
                    {f.filename}
                    <span>{t('{0} · pus {1}', fmtSize(f.size), roDateTime(f.createdAt))}</span>
                    {missed.length ? (
                      <span className="plan-missed">
                        {t(
                          'Pus după ce cererea a plecat la {0}: ei nu l-au primit.{1}',
                          missed.map((s) => s.supplier.name).join(', '),
                          autoSendAllowed()
                            ? t(' Retrimite-le cererea din „Mailurile de trimis”.')
                            : t(' Trimite-l separat.'),
                        )}
                      </span>
                    ) : null}
                  </div>
                  <a href={api.rfqFileUrl(rfq.id, f.id)}>{t('Descarcă')}</a>
                  <button className="btn small" onClick={() => void remove(f.id, f.filename)}>
                    {t('Șterge')}
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}
        <Drop busy={busy} onFiles={(f) => void add(f)} />
        <p className="hint">
          {files.length
            ? howTheyTravel(total)
            : isBuild()
              ? t(
                  'Planurile de arhitectură sau de cofraj, listele de cantități: furnizorii le primesc odată cu cererea.',
                )
              : t(
                  'Specificațiile, fișele tehnice, listele de cantități: furnizorii le primesc odată cu cererea.',
                )}
        </p>
      </div>
    </section>
  );
}
